import React from 'react';
import styled from 'styled-components';
import { Border, Color, Grid, Shadow, Type } from '../../../app/StyleGuide';
import { PrimaryButton } from '../../../components/Buttons';
import { Org } from '../../org';
import { ChurchAccountRedirect } from '../state/churchAccountRedirect';
import { redirectToCreateChurchAccount } from '../utils/redirectToCreateChurchAccount';
import { ChurchItem } from './ChurchItem';
import { ButtonRow, Header, Subtitle, Title } from './ConnectChurchAccountModalElements';

const ChurchItems = styled.div`
  display: grid;
  gap: ${Grid._5};
`;

const Section = styled.div`
  display: grid;
  gap: ${Grid._2};
  text-align: left;
`;

const SectionLabel = styled.span`
  font-size: ${Type.Scale._2};
  font-weight: ${Type.Weight.semibold};
  color: ${Type.Color.mediumDark};
`;

const MissingAccountCard = styled.div`
  display: grid;
  gap: ${Grid._2};
  width: 100%;
  min-height: 110px;
  align-content: center;
  border: 1px solid ${Color.Gray._20};
  border-radius: ${Border.radius};
  box-shadow: ${Shadow.small};
  background: ${Color.white};
  padding: ${Grid._4};
`;

const MissingAccountTitle = styled.h5`
  margin: 0;
  font-size: ${Type.Scale._2};
  font-weight: ${Type.Weight.semibold};
  color: ${Type.Color.dark};
`;

const MissingAccountMeta = styled.p`
  margin: 0;
  font-size: ${Type.Scale._1};
  color: ${Type.Color.medium};
`;

type ConnectChurchAccountCreateScreenProps = {
  org: Org;
  churchAccountRedirect: ChurchAccountRedirect;
  title?: string;
  subtitle?: string;
};

const getMembersText = (count: number | undefined) => {
  if (!count) return '';
  if (count === 1) return '1 Person';
  return `${count} People`;
};

export const ConnectChurchAccountCreateScreen = ({
  org,
  churchAccountRedirect,
  title,
  subtitle,
}: ConnectChurchAccountCreateScreenProps) => (
  <>
    <Header>
      <Title>{title || 'Sync Church Account'}</Title>
      <Subtitle>
        {subtitle ||
          'To sync your Ministry Grid church, a Lifeway church account is required. Information provided helps us improve your experience.'}
      </Subtitle>
    </Header>

    <ChurchItems>
      <Section>
        <SectionLabel>Ministry Grid Church</SectionLabel>
        <ChurchItem name={org.name} logo={org.logo} meta={getMembersText(org.members?.length)} />
      </Section>

      <Section>
        <SectionLabel>Lifeway Church Account</SectionLabel>
        <MissingAccountCard>
          <MissingAccountTitle>You're not linked to a church account</MissingAccountTitle>
          <MissingAccountMeta>
            Click below to create one. It will sync automatically to your Ministry Grid church.
          </MissingAccountMeta>
        </MissingAccountCard>
      </Section>
    </ChurchItems>

    <ButtonRow>
      <PrimaryButton
        onClick={() => redirectToCreateChurchAccount(churchAccountRedirect)}
        data-tracking-id="create-new-church-account"
      >
        Create Lifeway Church Account
      </PrimaryButton>
    </ButtonRow>
  </>
);
